import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { defaultIfEmpty } from 'rxjs';

import { AI_MODEL_OPTIONS } from '@blaze-shared/ai-chat-domain';
import { AuthService } from '@blaze-shared/auth-data-access';
import { getErrorMessage, LocalStorageService } from '@blaze-shared/platform-data-access';
import { SelectComponent, SharedModule } from '@blaze-shared/shared-feature';
import {
  ButtonComponent,
  ButtonIconComponent,
  DialogComponent,
  InplaceEditComponent,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { ThemesApiService } from '@blaze-shared/theming-data-access';
import { FileResult, UploaderFacade, UploaderOptions } from '@blaze-shared/uploader';

import { isThemeNameInUse } from '../theme-naming/theme-naming.utils';

import {
  CreateThemeDialogData,
  CreateThemeDialogResult,
  CreateThemeDialogStatus,
  CreateThemeForm,
  ThemeReferenceAttachment,
} from './create-theme-dialog.models';

const NO_RESPONSE = Symbol('no-response');

@UntilDestroy()
@Component({
  selector: 'blaze-create-theme-dialog',
  imports: [
    ButtonComponent,
    ButtonIconComponent,
    DialogComponent,
    FormsModule,
    InplaceEditComponent,
    MatIcon,
    ReactiveFormsModule,
    SelectComponent,
    SharedModule,
    TooltipDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './create-theme-dialog.component.html',
  styleUrl: './create-theme-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateThemeDialogComponent implements AfterViewInit {
  private readonly dialogData = inject<CreateThemeDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<CreateThemeDialogComponent, CreateThemeDialogResult>>(MatDialogRef);
  private readonly themesApiService = inject(ThemesApiService);
  private readonly uploaderFacade = inject(UploaderFacade);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly authService = inject(AuthService);

  readonly submitInputRef = viewChild<ElementRef<HTMLFormElement>>('submitInput');
  readonly themeNameField = viewChild<InplaceEditComponent>('themeNameField');

  /** The name as it currently reads in the field, which while editing is ahead of the committed one. */
  private readonly editedName = signal(this.dialogData.name);

  readonly DialogStatus = CreateThemeDialogStatus;
  readonly modelOptions = AI_MODEL_OPTIONS;

  readonly form: FormGroup<CreateThemeForm>;
  isEditingName = false;
  readonly status = signal<CreateThemeDialogStatus>(CreateThemeDialogStatus.Form);
  readonly creatingTheme = signal<boolean>(false);
  readonly error = signal<string | undefined>(undefined);
  readonly attachments = signal<ThemeReferenceAttachment[]>([]);

  readonly isNameInUse = computed(() =>
    isThemeNameInUse(this.editedName(), this.dialogData.usedNames),
  );

  readonly canCreateTheme = computed(
    () => this.attachments().length > 0 && !this.creatingTheme() && !this.isNameInUse(),
  );

  get nameValue(): string {
    return this.form.controls.name.value;
  }

  get ideaValue(): string {
    return this.form.controls.idea.value.trim();
  }

  get showAdvancedAiOptions(): boolean {
    return this.authService.isCurrentUserAccountRoot();
  }

  constructor() {
    this.form = new FormGroup({
      name: new FormControl(this.dialogData.name, { nonNullable: true }),
      idea: new FormControl('', { nonNullable: true }),
      model: new FormControl<string | null>(null),
    });
  }

  ngAfterViewInit(): void {
    this.submitInputRef()?.nativeElement.focus();
  }

  editNameValue(input: InplaceEditComponent): void {
    this.isEditingName = true;
    input.edit();
  }

  draftNameValue(value: string): void {
    this.editedName.set(value.trim());
  }

  commitNameValue(value: string): void {
    this.isEditingName = false;
    this.editedName.set(value);
    this.form.controls.name.setValue(value);
  }

  cancelNameEditing(): void {
    this.isEditingName = false;
    this.editedName.set(this.nameValue);
  }

  submitCreateTheme(): void {
    if (!this.canCreateTheme()) {
      return;
    }

    const { name = '', model } = this.form.value;

    this.creatingTheme.set(true);
    this.error.set(undefined);
    this.status.set(CreateThemeDialogStatus.CreatingAI);

    this.themesApiService
      .createThemeWithAI({
        projectId: this.dialogData.projectId,
        model: model || undefined,
        attachments: this.attachments().map(({ mime, url }) => ({ mime, url })),
        themeName: name || undefined,
        prompt: this.ideaValue || undefined,
      })
      .pipe(defaultIfEmpty(NO_RESPONSE), untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (response === NO_RESPONSE) {
            this.showError('Theme generation failed. Please try again.');
            return;
          }
          if (!response.themeId) {
            this.showError('Theme generation failed. Please try again.');
            return;
          }

          this.dialogRef.close({ themeId: response.themeId });
        },
        error: (error) => {
          this.showError(getErrorMessage(error));
        },
      });
  }

  uploadAttachments(): void {
    const options: UploaderOptions = {
      accountId: this.localStorageService.getAccountId(),
      isMultipleSelection: true,
      acceptedFileTypes: [
        ...this.uploaderFacade.supportedImageTypes,
        ...this.uploaderFacade.supportedPdfTypes,
      ],
    };

    this.uploaderFacade
      .upload(options)
      .pipe(untilDestroyed(this))
      .subscribe((results) => {
        const uploaded = this.toAttachments(results);
        if (uploaded.length === 0) {
          return;
        }
        this.attachments.update((current) => [...current, ...uploaded]);
      });
  }

  removeAttachment(attachment: ThemeReferenceAttachment): void {
    this.attachments.update((current) => current.filter((a) => a.url !== attachment.url));
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private showError(message: string): void {
    this.status.set(CreateThemeDialogStatus.Form);
    this.error.set(message);
    this.creatingTheme.set(false);
  }

  private toAttachments(results: void | FileResult[]): ThemeReferenceAttachment[] {
    if (!results) {
      return [];
    }
    return results
      .filter((result): result is FileResult & { url: string } => Boolean(result.url))
      .map((result) => ({
        url: result.url,
        mime: result.mimetype ?? '',
        filename: result.filename,
      }));
  }
}
